/**
 * TripQuickSummaryBar — compact vehicle / driver / departure / capacity strip.
 */
import styled from "styled-components";
import {
  LocalShippingOutlined,
  PersonOutline,
  CalendarTodayOutlined,
  Inventory2Outlined,
} from "@mui/icons-material";
import {
  formatCapacityFraction,
  formatTripDeparture,
  getCapacityPercent,
  getDriverLabel,
  getDriverPhone,
  getVehicleLabel,
} from "../utils/tripFormatters.js";

const Bar = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  padding: 16px 20px;
  margin-bottom: 24px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Item = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
`;

const IconWrap = styled.span`
  display: inline-flex;
  color: #64748b;
  margin-top: 2px;
`;

const Content = styled.div`
  min-width: 0;
`;

const Label = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 2px;
`;

const Value = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SubValue = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
`;

const TripQuickSummaryBar = ({ trip, summary = null }) => {
  const capacityPercent = getCapacityPercent(summary || trip);

  return (
    <Bar data-testid="trip-quick-summary-bar">
      <Item>
        <IconWrap>
          <LocalShippingOutlined fontSize="small" />
        </IconWrap>
        <Content>
          <Label>Vehicle</Label>
          <Value>{getVehicleLabel(trip)}</Value>
        </Content>
      </Item>
      <Item>
        <IconWrap>
          <PersonOutline fontSize="small" />
        </IconWrap>
        <Content>
          <Label>Driver</Label>
          <Value>{getDriverLabel(trip)}</Value>
          {getDriverPhone(trip) ? <SubValue>{getDriverPhone(trip)}</SubValue> : null}
        </Content>
      </Item>
      <Item>
        <IconWrap>
          <CalendarTodayOutlined fontSize="small" />
        </IconWrap>
        <Content>
          <Label>Departure</Label>
          <Value>{formatTripDeparture(trip.departureDate)}</Value>
        </Content>
      </Item>
      <Item>
        <IconWrap>
          <Inventory2Outlined fontSize="small" />
        </IconWrap>
        <Content>
          <Label>Capacity</Label>
          <Value>{formatCapacityFraction(summary || trip)}</Value>
          <SubValue>{capacityPercent}% used</SubValue>
        </Content>
      </Item>
    </Bar>
  );
};

export default TripQuickSummaryBar;
